/* eslint-disable camelcase */
import { Camelized, decamelizeKeys } from "humps";
import omit from "lodash/omit";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import api from "PFCore/api";
import { decamelizeKeysExcept } from "PFCore/helpers/keys_camelization_utils";
import { ActivityWarning } from "PFCore/hooks/queries/activity";
import { BookingPartPayload } from "PFCore/hooks/queries/bookings/part_update/use_booking_part_update";
import { CalendarGranularity } from "PFCore/hooks/queries/profile/use_profiles_availabilities";
import {
  Booking,
  BookingApproval,
  BookingTemplate,
  CalendarRange,
  Collection,
  Currency,
  Filters,
  Id,
  PaginatedCollection,
  QueryParams,
  RequirementType,
  Value
} from "PFTypes";
import { EmptyObject } from "type-fest";

import { BookingResponse } from "../components/booking_form/use_handle_submit/use_handle_submit";
import { Overbooking } from "../types";

export type FetchBookings = {
  id?: number | number[];
  profileId?: number;
  activityId?: number | number[] | boolean;
  bookingTemplateId?: number;
  bookingCategoryId?: number[];
  dateRange?: { min: string; max: string };
  withDurationBreakdown?: boolean;
  perPage?: number;
};

export const fetchBookings = ({
  id,
  profileId,
  activityId,
  bookingTemplateId,
  bookingCategoryId,
  dateRange,
  perPage = PER_PAGE_UNPAGINATED
}: FetchBookings): Promise<PaginatedCollection<Omit<Booking, "activity">[]>> =>
  api({
    url: "/booking_module/bookings/index",
    method: "POST",
    data: {
      id,
      activityId,
      profileId,
      perPage,
      ...(bookingTemplateId ? { bookingTemplateId } : {}),
      ...(bookingCategoryId ? { bookingCategoryId } : {}),
      ...(dateRange ? { dateRange } : {})
    }
  }).then((resp) => decamelizeKeys(resp) as PaginatedCollection<Omit<Booking, "activity">[]>);

export const fetchBookingsWithDurationBreakdown = ({
  id,
  profileId,
  activityId,
  bookingTemplateId,
  bookingCategoryId,
  dateRange,
  perPage = PER_PAGE_UNPAGINATED
}: FetchBookings): Promise<PaginatedCollection<Omit<Booking, "activity">[]>> =>
  api({
    url: "booking_module/bookings/duration_breakdown",
    method: "GET",
    params: {
      id,
      activityId,
      profileId,
      perPage,
      ...(bookingTemplateId ? { bookingTemplateId } : {}),
      ...(bookingCategoryId ? { bookingCategoryId } : {}),
      ...(dateRange ? { dateRange } : {})
    }
  }).then((resp) => decamelizeKeys(resp) as PaginatedCollection<Omit<Booking, "activity">[]>);

export const fetchBooking = (id) =>
  api({
    url: `booking_module/bookings/${id}`
    // TODO: [PROF-4641] - remove decamelizeKeys when app is ready
    // converted to axios api to be working on outlook plugin
  }).then((resp) => decamelizeKeys(resp) as Booking);

export type DeleteBookingProps = {
  id?: number;
  profileId?: number;
  dateRange?: CalendarRange;
  activityId?: number;
};

export const deleteBooking = (data: DeleteBookingProps): Promise<void> =>
  api({
    url: `booking_module/bookings`,
    method: "DELETE",
    data
  });

export type BookingDataToUpdate = Partial<
  Camelized<
    Pick<
      Booking,
      | "start_date"
      | "end_date"
      | "requirement_type"
      | "requirement_value"
      | "booking_category_id"
      | "title"
      | "description"
      | "overrides_calendar"
      | "overrides_non_working_days"
      | "phase_source_id"
      | "booking_group_id"
      | "profile_id"
    >
  >
>;

export type CreateBookingPayload = ({ profileId: number; activityId?: number } | { searchId: number }) &
  Camelized<{
    sourceType: "ui";
    bookings: BookingDataToUpdate[];
  }>;

export const createBooking = (payload: CreateBookingPayload) =>
  api({
    url: `booking_module/bookings`,
    method: "POST",
    data: payload
  });

export type UpdateBookingPayload = BookingDataToUpdate & { id?: number };

export const updateBooking = (payload: UpdateBookingPayload) =>
  api({
    url: `booking_module/bookings/${payload.id}`,
    method: "PUT",
    data: omit(payload, "id")
  });

export type UpdateBookingsPayload = (
  | { profileId: number; activityId?: number }
  | { bookingGroupId: number }
) &
  Camelized<{
    bookings: (BookingDataToUpdate & Pick<Booking, "id">)[];
  }>;

export const updateManyBookings = (payload: UpdateBookingsPayload): Promise<Collection<BookingResponse[]>> =>
  api({
    url: "booking_module/bookings/update_many",
    method: "PUT",
    data: payload
  });

export const updateBookingPart = (id: number, payload: BookingPartPayload) =>
  api({
    url: `booking_module/bookings/${id}/update_part`,
    method: "PUT",
    data: payload
  });

type FetchProfileWarnings = {
  profileId: number;
  activityId?: number;
  dateRange?: CalendarRange;
  newBooking?: boolean;
};
export const fetchProfileWarnings = ({
  profileId,
  dateRange,
  newBooking = false,
  activityId
}: FetchProfileWarnings) =>
  api({
    url: "booking_module/profile_warnings",
    params: {
      profileId,
      activityId,
      dateRange,
      newBooking
    }
  });

export const fetchActivityWarnings = (activityId: number): Promise<ActivityWarning[]> =>
  api({
    url: "booking_module/activity_warnings",
    params: { activityId }
  });

export const fetchProfileAvailabilities = ({
  profileId,
  dateRange,
  perPage = PER_PAGE_UNPAGINATED,
  page = 1,
  calendarGranularity
}) =>
  api({
    url:
      calendarGranularity === CalendarGranularity.Daily
        ? "booking_module/availabilities"
        : "booking_module/availabilities/weekly",
    params: {
      profileId,
      dateRange,
      page,
      perPage
    }
  });

type FetchWorkforceMetrics = {
  dateRange: CalendarRange;
  filters?: Filters<Value>;
  previousSearchId?: number;
};
type WorkforceMetricsData = {
  availableMinutes: number | null;
  billableUtilization: number | null;
  bookedMinutes: number | null;
  nominalWorkingMinutes: number | null;
  totalUtilization: number | null;
};

export const fetchWorkforceMetrics = ({
  dateRange,
  filters,
  previousSearchId
}: FetchWorkforceMetrics): Promise<WorkforceMetricsData> =>
  api({
    url: "booking_module/utilizations/index",
    method: "post",
    data: {
      dateRange,
      filters,
      previousSearchId
    }
  });

export type PotentialWarningsResponse = {
  overbookings: Overbooking[];
  misalignments: ActivityWarning[];
  exeedsCalendarHours: boolean;
  includesNonWorkingDays: boolean;
  overbookingIntersectionsDates: string[];
};

export type PotentialWarningsDateRange = {
  min: string;
  max: string;
};

export const fetchPotentialWarningsForSingleBookings = (
  profileId: number,
  bookings: Partial<Booking>[],
  activityId?: number,
  dateRange?: PotentialWarningsDateRange
): Promise<PotentialWarningsResponse> =>
  api({
    url: "booking_module/bookings/potential_warnings",
    method: "post",
    data: {
      profileId,
      bookings,
      ...(!!activityId && { activityId }),
      ...(dateRange !== undefined && { dateRange })
    }
  });
export const fetchPotentialWarningsForBookingTemplates = (
  profileId: number,
  bookingTemplates: Partial<BookingTemplate>[]
): Promise<PotentialWarningsResponse> =>
  api({
    url: "booking_module/booking_templates/potential_warnings",
    method: "post",
    data: {
      profileId,
      bookingTemplates
    }
  });

export const fetchBookingHistory = ({ bookingId, page, perPage }) =>
  api({
    url: `booking_module/bookings/${bookingId}/history`,
    params: {
      page,
      perPage
    }
  });

export type BookingTemplateDataToUpdate = Camelized<
  Pick<
    BookingTemplate,
    | "start_date"
    | "end_date"
    | "start_time"
    | "end_time"
    | "booking_category_id"
    | "title"
    | "description"
    | "overrides_calendar"
    | "overrides_non_working_days"
    | "wday_mask"
  >
>;

export type CreateBookingTemplatePayload = ({ profileId: number } | { searchId: number }) &
  BookingTemplateDataToUpdate;

export const createBookingTemplate = (payload: CreateBookingTemplatePayload) =>
  api({
    url: `booking_module/booking_templates`,
    method: "POST",
    data: payload
  });

export const fetchBookingTemplate = (id?: number) =>
  api({
    url: `booking_module/booking_templates/${id}`,
    method: "GET"
  }).then((resp) => decamelizeKeys(resp) as BookingTemplate);

export type UpdateBookingTemplatePayload = {
  id: number;
  profileId: number;
} & BookingTemplateDataToUpdate &
  Partial<{ dateRange: CalendarRange; bookingGroupId: number }>;

export const updateBookingTemplate = (payload: UpdateBookingTemplatePayload) =>
  api({
    url: `booking_module/booking_templates${payload.bookingGroupId ? "" : `/${payload.id}`}`,
    method: "PUT",
    data: omit(payload, "id")
  });

export const deleteBookingTemplate = (id?: number, payload: Omit<DeleteBookingProps, "id"> = {}) =>
  api({
    url: `booking_module/booking_templates${id ? `/${id}` : ""}`,
    method: "DELETE",
    data: payload
  });

export type BookingApprovalCounters = { approved?: number; pending?: number; rejected?: number };
export type BookingApprovalsCollection = PaginatedCollection<
  BookingApproval[],
  EmptyObject,
  { bookingApprovalsStateTally: BookingApprovalCounters }
>;

export const fetchBookingApprovals = (params: QueryParams = {}) =>
  api<BookingApprovalsCollection>({
    url: "booking_module/booking_approvals",
    params
  }).then((res) => decamelizeKeysExcept(res, ["meta"]));

type ApproveBookingApprovalPayload = {
  note?: string | null;
  bookingCategoryId?: number | null;
};

export const approveBookingApproval = (id: Id, data: ApproveBookingApprovalPayload) =>
  api({
    url: `booking_module/booking_approvals/${id}/approve`,
    method: "PUT",
    data
  });

export const rejectBookingApproval = (id: Id, data: { note?: string | null }) =>
  api({
    url: `booking_module/booking_approvals/${id}/reject`,
    method: "PUT",
    data
  });

type RequestBookingApprovalPayload = {
  assigneeId: Id;
  requestReason?: string | null;
};

export const requestBookingApproval = (id: Id, data: RequestBookingApprovalPayload) =>
  api({
    url: `booking_module/booking_approvals/${id}/request_approval`,
    method: "PUT",
    data
  });

export const fetchAdminBookingCategories = (page, perPage, params) =>
  api({
    url: `admin/booking_module/booking_categories`,
    params: {
      page,
      perPage,
      ...params
    }
  });

type BookingFromAvailability = {
  start_date: string;
  end_date: string;
  duration: number;
  overrides_calendar: boolean;
  overrides_non_working_days: boolean;
  requirement_value: number;
  requirement_type: RequirementType;
  phase_source_id: string;
};

export type BookingsFromAvailabilityResponse = {
  ranges: BookingFromAvailability[];
  title?: string;
  description?: string;
};

export const fetchBookingsFromAvailability = (activityId?: number, profileId?: number) =>
  api({
    url: `booking_module/activities/${activityId}/query_bookings`,
    method: "GET",
    params: {
      profileId
    }
  }).then((resp) => decamelizeKeys(resp) as BookingsFromAvailabilityResponse);

export type BookingEngagementsEconomicsResponse = Collection<{
  id: number;
  budget: number | null;
  cost: { value: number; currency: Currency };
}>;

export const fetchBookingEngagementsEconomics = (engagement_id: Id[]) =>
  api<BookingEngagementsEconomicsResponse>({
    url: "booking_module/engagements/economics",
    params: { engagement_id }
  });

type DemandRelatedFilters = {
  activityId: number;
  profileId: number;
};

type NonDemandRelatedFilters = {
  profileId: number;
  bookingCategoryId: number;
};

export type ReplaceRangeFilters = DemandRelatedFilters | NonDemandRelatedFilters;

export type ReplaceRangeItem = {
  startDate: string;
  endDate: string;
  requirementValue: number;
  requirementType: string;
  bookingCategoryId: number;
};

export type ReplaceBookingsRangePayload = {
  filters: ReplaceRangeFilters;
  data: ReplaceRangeItem[];
};

type ReplaceRangeBooking = {
  id: number;
};

export type ReplaceBookingsRangeResponse = {
  createdBookings: ReplaceRangeBooking[];
  updatedBookings: ReplaceRangeBooking[];
  deletedBookings: ReplaceRangeBooking[];
};

export const replaceBookingsRange = (
  payload: ReplaceBookingsRangePayload
): Promise<ReplaceBookingsRangeResponse> =>
  api({
    url: "booking_module/bookings/replace_in_range",
    method: "POST",
    data: payload
  });
